import React from 'react';
import css from './DateTimeStep.module.css';
import { WizardStepWrapper } from '../step';
import {
  H2,
  LoadingSpinner,
  LoadingSpinnerSize,
  StackedColumn,
  StackedColumnSpacing,
  StackedRow,
  StackedRowSpacing,
} from '../../../../core';
import {
  CernerSlot,
  DateTimeDetails,
  DateTimeFilter,
  LocationDetails,
  ProviderDetails,
  ProviderSummary,
  SchedulingDetails,
  SlotDateGroup,
} from '../../../../api/models';
import { SearchFilter } from './searchFilter';
import {
  ResponsiveSizeUtility,
  getLocationOptionsFromAppointmentType,
} from '../../../../utils';
import { SearchResults } from './searchResults';
import { SimilarProviders } from './similarProviders';
import { PresenterState } from './PresenterState';
import { DateTimeStepFooter } from './dateTimeStepFooter';
import { GenericError } from '../../../genericError';
import { useScreenSize } from '@mchs/web-core';

export interface DateTimeStepPresenterProps {
  filter: DateTimeFilter;
  schedulingDetails: SchedulingDetails;
  searchResults: SlotDateGroup[];
  alternateProviders: ProviderSummary[];
  state: PresenterState;
  onFilterChanged: (date: Date | null | undefined, locationId: string) => void;
  onCareProviderSelected: (
    newProviderDetails: ProviderDetails,
    availableAppointmentSlots?: SlotDateGroup[]
  ) => void;
  onContinue: (
    dateTimeDetails: DateTimeDetails,
    locationDetails: LocationDetails
  ) => void;
}

const DateTimeStepPresenter: React.FC<DateTimeStepPresenterProps> = (
  props: DateTimeStepPresenterProps
) => {
  const screenSize = useScreenSize();

  const narrowWidth = ResponsiveSizeUtility.isNarrowWidth(screenSize.width);

  const onSlotSelected = (
    appointmentDate?: Date,
    slot?: CernerSlot,
    locationId?: string,
    locationName?: string
  ) => {
    props.onContinue(
      {
        appointmentDate: appointmentDate,
        slot: slot,
      },
      { searchLocationId: locationId, searchLocationName: locationName }
    );
  };

  const filterChanged = (date: Date | null | undefined, locationId: string) => {
    props.onFilterChanged(date, locationId);
  };

  const locationOptions = getLocationOptionsFromAppointmentType(
    props.schedulingDetails.providerDetails?.appointmentTypeList,
    props.schedulingDetails.appointmentInfo?.appointmentTypeId
  );

  return (
    <WizardStepWrapper
      specialFooter={
        props.state === PresenterState.Ready && narrowWidth ? (
          <DateTimeStepFooter
            alternateProviders={props.alternateProviders}
            filter={props.filter}
            onCareProviderSelected={props.onCareProviderSelected}
          />
        ) : null
      }
      stickyWhenNarrow={true}
    >
      {props.state === PresenterState.SwappingProviders ||
      props.state === PresenterState.Loading ? (
        <LoadingSpinner
          label="Loading, please wait..."
          size={LoadingSpinnerSize.Medium}
          delayInMs={0}
        />
      ) : props.state === PresenterState.Error ? (
        <GenericError />
      ) : (
        <StackedRow fillHeight={true} spacing={StackedRowSpacing.Wide}>
          <StackedColumn fillHeight={true} spacing={StackedColumnSpacing.Wide}>
            <H2 text="Select a date and time" />
            <SearchFilter
              date={props.filter.date}
              locationId={props.filter.locationId}
              locationList={locationOptions}
              narrowWidth={narrowWidth}
              filterChanged={filterChanged}
            />
            <SearchResults
              searchResults={props.searchResults}
              filter={props.filter}
              onSlotSelected={onSlotSelected}
            />
          </StackedColumn>
          {!narrowWidth ? (
            <>
              <div className={css.verticalLineContainer}>
                <div className={css.verticalLine} />
              </div>
              <div className={css.similarProvidersContainer}>
                <StackedColumn>
                  <H2 text="Similar care providers" />
                  <SimilarProviders
                    alternateProviders={props.alternateProviders}
                    filter={props.filter}
                    onCareProviderSelected={props.onCareProviderSelected}
                  />
                </StackedColumn>
              </div>
            </>
          ) : null}
        </StackedRow>
      )}
    </WizardStepWrapper>
  );
};

export default DateTimeStepPresenter;
